// import { CloudCog } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
// import bgImage from "../assets/build.svg"; // fallback image if needed
import overlayImage from "../assets/Gradient Fractal (1).png";


type PageHeaderMedia =
  | { type: "video"; src: string; poster?: string }
  | { type: "image"; src: string };

interface PageHeaderProps {
  title?: string;
  media?: PageHeaderMedia;
  onMediaLoad?: () => void;
}

const PageHeaderVideo = ({ title = "About Us", media, onMediaLoad }: PageHeaderProps) => {
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Handler for when media is loaded
  const handleMediaLoaded = () => {
    setMediaLoaded(true);
    if (onMediaLoad) onMediaLoad();
  };

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoadVideo(true);
          }
        });
      },
      { threshold: 0.35 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (shouldLoadVideo && videoRef.current) {
      videoRef.current.play().catch(() => {
        // ignore autoplay restrictions
      });
    }
  }, [shouldLoadVideo]);

  return (
    <div ref={containerRef} className="relative h-[500px] md:h-[858px] flex items-center overflow-hidden">
      {/* Static gray placeholder until media is loaded */}
      {!mediaLoaded && (
        <div
          className="absolute top-0 left-0 w-full h-full z-0 bg-gray-200 animate-none"
          style={{ minHeight: "500px", minWidth: "100%" }}
        />
      )}
      {/* Background Media */}
      {media?.type === "video" ? (
        <div>
          <video
            ref={videoRef}
            className="absolute top-0 left-0 w-full h-full object-cover z-0 scale-105 md:scale-110 transition-transform duration-700"
            src={shouldLoadVideo ? media.src : undefined}
            autoPlay={shouldLoadVideo}
            loop
            muted
            playsInline
            preload={shouldLoadVideo ? "metadata" : "none"}
            poster={media.poster || "/assets/build.svg"}
            style={{ objectFit: "cover" }}
            onLoadedData={handleMediaLoaded}
          />
          {/* Black tint overlay */}
          <div className="absolute inset-0 bg-black opacity-20 z-10" />
        </div>
      ) : null}
      {/* Overlay image for bottom 30% */}
      {media && media?.type === "video" && (
        <img
          src={overlayImage}
          alt="overlay"
          className="pointer-events-none absolute left-0 w-full z-10"
          style={{
            bottom: 0,
            height: "50%",
            opacity: 0.99, // Adjust for desired transparency
          }}
        />
      )}
      {/* Overlay */}
      <div className="absolute top-0 left-0 w-full h-full  z-10" />
      {/* Title */}
      <h1
        className="
          font-utility           /* your Utility Pro family */
          font-medium  
          text-[40px]             /* weight 500 */
          md:text-[100px]           /* exactly 100px size */
          leading-[120%]         /* 120% line-height */
          tracking-[-1%]         /* –1% letter-spacing */
          relative z-20 text-white
          px-[30px]
          md:px-[80px]
          whitespace-pre-line    /* honor the \n in your string */
        "
      >
        {title}
      </h1>
    </div>
  );
};

export default PageHeaderVideo;
